import React, { useState, useEffect, useMemo } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "../../Images/Header/logo.png";
import EN from "../../Images/Header/EN-Icon.png";
import LangDropdown from "../../Components/Language-Dropdown";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import userIcon from "../../Images/SignupForm/user-icon.png";
import { Popover } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { getDriverOnboardingPath } from "../../utils/auth_storage";
import "../../CSS/Header/index.css";

const PUBLIC_NAV_LINKS = [
  { to: "/", labelKey: "menu_1", end: true },
  { to: "/about-us", labelKey: "menu_2" },
  { to: "/safety", labelKey: "menu_5" },
];

const GUEST_NAV_LINKS = [
  { to: "/user-signup", labelKey: "menu_3" },
  { to: "/driver-signup", labelKey: "menu_4" },
];

const DRIVER_PATH_PATTERN =
  /^\/(driver-|personaldocument|cardocument|update-password-driver)/;

function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { session, isAuthenticated, isDriver, isRider, logout } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const isDriverContext = DRIVER_PATH_PATTERN.test(location.pathname);
  const loginPath = isDriverContext ? "/driver-login" : "/user-login";
  const signupPath = isDriverContext ? "/driver-signup" : "/user-signup";

  const navLinks = useMemo(() => {
    const links = [...PUBLIC_NAV_LINKS];
    if (!isAuthenticated) {
      links.splice(2, 0, ...GUEST_NAV_LINKS);
    }
    if (isAuthenticated && isDriver) {
      links.push({
        to: getDriverOnboardingPath(session?.profile),
        labelKey: "header_menu_documents",
      });
    }
    return links;
  }, [isAuthenticated, isDriver, session?.profile]);

  const userMenuItems = useMemo(() => {
    const items = [];
    if (isDriver) {
      items.push({
        type: "link",
        to: getDriverOnboardingPath(session?.profile),
        labelKey: "header_menu_documents",
      });
    }
    items.push(
      { type: "link", to: "/help-support", labelKey: "header_menu_help" },
      { type: "link", to: "/contact", labelKey: "header_menu_contact" },
      {
        type: "link",
        to: "/delete-account",
        labelKey: "header_menu_delete_account",
      },
      { type: "action", labelKey: "header_menu_logout", action: "logout" }
    );
    return items;
  }, [isDriver, session?.profile]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 8);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileOpen]);

  const handleLanguageChange = (event) => {
    const language = event.target.value;
    if (!language) {
      return;
    }
    i18next.changeLanguage(language);
    localStorage.setItem("preferredLanguage", language);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    navigate("/");
  };

  const handleUserMenuAction = (item) => {
    if (item.action === "logout") {
      handleLogout();
    }
  };

  const isUserMenuOpen = Boolean(anchorEl);

  const toggleMobileMenu = () => {
    setIsMobileOpen((prev) => !prev);
  };

  const getNavLinkClass = ({ isActive }) =>
    `site-header__link${isActive ? " site-header__link--active" : ""}`;

  const roleLabel = isDriver
    ? t("header_role_driver")
    : isRider
      ? t("header_role_rider")
      : "";

  return (
    <header className={`site-header${isScrolled ? " site-header--scrolled" : ""}`}>
      <div className="site-header__inner">
        <Link className="site-header__logo" to="/" aria-label="Comfort Trip home">
          <img src={Logo} alt="Comfort Trip" />
        </Link>

        <button
          type="button"
          className={`site-header__toggle${isMobileOpen ? " site-header__toggle--open" : ""}`}
          onClick={toggleMobileMenu}
          aria-expanded={isMobileOpen}
          aria-label="Toggle navigation menu"
        >
          <span className="site-header__toggle-bar" />
          <span className="site-header__toggle-bar" />
          <span className="site-header__toggle-bar" />
        </button>

        <nav
          className={`site-header__nav${isMobileOpen ? " site-header__nav--open" : ""}`}
          aria-label="Main navigation"
        >
          {navLinks.map((item) => (
            <NavLink
              key={item.to + item.labelKey}
              to={item.to}
              end={item.end}
              className={getNavLinkClass}
            >
              {t(item.labelKey)}
            </NavLink>
          ))}

          <div className="site-header__aside">
            <div className="site-header__lang">
              <img className="site-header__lang-icon" src={EN} alt="" />
              <LangDropdown onChange={handleLanguageChange} />
            </div>

            <div className="site-header__actions">
              {isAuthenticated ? (
                <>
                  <button
                    type="button"
                    className="site-header__user-btn"
                    onClick={handleUserMenuOpen}
                    aria-label={t("header_user_menu_aria")}
                    aria-haspopup="true"
                    aria-expanded={isUserMenuOpen}
                  >
                    <img src={userIcon} alt="" />
                    <span className="site-header__user-btn-label">
                      {session?.displayName}
                    </span>
                  </button>
                  <Popover
                    id="header-user-menu"
                    open={isUserMenuOpen}
                    anchorEl={anchorEl}
                    onClose={handleUserMenuClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    classes={{ paper: "site-header__user-menu" }}
                  >
                    <div className="site-header__user-menu-header">
                      <p className="site-header__user-menu-name">
                        {session?.displayName}
                      </p>
                      {session?.email && (
                        <p className="site-header__user-menu-email">
                          {session.email}
                        </p>
                      )}
                      {roleLabel && (
                        <span className="site-header__user-menu-role">
                          {roleLabel}
                        </span>
                      )}
                    </div>
                    {userMenuItems.map((item) =>
                      item.type === "link" ? (
                        <Link
                          key={item.labelKey}
                          to={item.to}
                          className="site-header__user-menu-item"
                          onClick={handleUserMenuClose}
                        >
                          {t(item.labelKey)}
                        </Link>
                      ) : (
                        <span
                          key={item.labelKey}
                          className="site-header__user-menu-item site-header__user-menu-item--danger"
                          onClick={() => handleUserMenuAction(item)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              handleUserMenuAction(item);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          {t(item.labelKey)}
                        </span>
                      )
                    )}
                  </Popover>
                </>
              ) : (
                <>
                  <Link
                    to={loginPath}
                    className="site-header__btn site-header__btn--ghost"
                  >
                    {t("navbar_btn1")}
                  </Link>
                  <Link
                    to={signupPath}
                    className="site-header__btn site-header__btn--primary"
                  >
                    {t("navbar_btn2")}
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>
      </div>
    </header>
  );
}

export default Header;
